import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties, ComputedRef, Ref } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { CustomerListItem } from '@entities/customer'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { useCustomersStore } from './store'
import { getCustomerUrl } from './customer-links'
import { useCustomerBehalf } from '../../api/list/behalf.hook'
import { useCustomerPrivileges } from '../customer-privileges.hook'

interface UseCustomerActionsOptions {
  customer: ComputedRef<CustomerListItem>
}

interface CustomerActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  canManage: ComputedRef<boolean>
  canDelete: ComputedRef<boolean>
  toggle: () => void
  openCustomer: () => Promise<void>
  actOnBehalf: () => Promise<void>
  askDelete: () => void
}

export const useCustomerActions = ({ customer }: UseCustomerActionsOptions): CustomerActionsReturn => {
  const store = useCustomersStore()
  const { canManageCustomers, canDeleteCustomers } = useCustomerPrivileges()

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const openCustomer = async (): Promise<void> => {
    isOpen.value = false
    await navigateTo(getCustomerUrl(customer.value.id))
  }

  /**
   * The link arrives from a POST, so the tab is opened inside the click handler (popup blockers) and
   * pointed at the link once it is known.
   */
  const actOnBehalf = async (): Promise<void> => {
    isOpen.value = false

    const popup = window.open('', '_blank')
    const url = await useCustomerBehalf(customer.value.id)

    if (!popup) return

    if (!url) {
      popup.close()
      return
    }

    popup.opener = null
    popup.location.href = url
  }

  const askDelete = (): void => {
    isOpen.value = false
    store.setDeleteCustomerId(customer.value.id)
  }

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    canManage: canManageCustomers,
    canDelete: canDeleteCustomers,
    toggle,
    openCustomer,
    actOnBehalf,
    askDelete,
  }
}
